import {
  useMaskableField,
  useRegisterVisibilityField,
  type MaskableFieldOptions,
  type RegisterFieldOptions,
} from "@/context/FormFieldVisibilityContext";
import { MASKABLE_FIELD_MINIMIZED_CLASS } from "@/lib/maskable-field-styles";
import { cn } from "@/lib/utils";
import { isValidElement, type ReactNode } from "react";
import { FieldVisibilityToggle } from "./FieldVisibilityToggle";
import { MaskableFieldTrailing } from "./MaskableFieldTrailing";

/** Best-effort extraction of plain text from a label node (string or <span>text</span>). */
function reactNodeToText(node: ReactNode): string | undefined {
  if (node == null || typeof node === "boolean") return undefined;
  if (typeof node === "string") return node.trim() || undefined;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) {
    const parts = node.map(reactNodeToText).filter(Boolean);
    return parts.length ? parts.join(" ") : undefined;
  }
  if (isValidElement(node)) {
    return reactNodeToText((node.props as { children?: ReactNode }).children);
  }
  return undefined;
}

type MaskableFieldHeaderProps = MaskableFieldOptions & {
  fieldName: string;
  label: ReactNode;
  sublabel?: ReactNode;
  labelClassName?: string;
  trailing?: ReactNode;
  className?: string;
};

export function MaskableFieldHeader({
  fieldName,
  label,
  sublabel,
  labelClassName,
  trailing,
  nonMaskable,
  maskable,
  className,
}: MaskableFieldHeaderProps) {
  const { enabled, expanded, minimized, canToggle, toggleVisibility } =
    useMaskableField(fieldName, { nonMaskable, maskable });
  useRegisterVisibilityField(fieldName, {
    nonMaskable,
    maskable,
    label: reactNodeToText(label),
  });

  if (!enabled || nonMaskable) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-2",
        className ?? "mb-1.5",
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div
          className={cn(
            "maskable-field-label text-sm font-medium font-['Raleway'] leading-normal whitespace-nowrap overflow-hidden text-ellipsis",
            minimized ? MASKABLE_FIELD_MINIMIZED_CLASS : "text-slate-700",
            labelClassName,
          )}
        >
          {label}
        </div>
        {sublabel != null && (
          <div
            className={cn(
              minimized &&
                cn(
                  MASKABLE_FIELD_MINIMIZED_CLASS,
                  "[&_*]:text-muted-foreground",
                ),
            )}
          >
            {sublabel}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {trailing != null && (
          <MaskableFieldTrailing minimized={minimized}>
            {trailing}
          </MaskableFieldTrailing>
        )}
        {canToggle && (
          <FieldVisibilityToggle
            expanded={expanded}
            minimized={minimized}
            onToggle={toggleVisibility}
          />
        )}
      </div>
    </div>
  );
}

export function useMaskableFieldExpanded(
  fieldName: string,
  opts?: RegisterFieldOptions,
) {
  const mask = useMaskableField(fieldName, opts);
  useRegisterVisibilityField(fieldName, opts);
  return {
    ...mask,
    showControl: !mask.enabled || mask.expanded,
  };
}
